import React, { useState } from "react";
import { GitBranch, Star, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import Swal from "sweetalert2";
import "./index.css";

interface PlanCardsProps {
  allPlans: any[];
  selectedPlanId: string | null;
  handlePlanSelect: (planId: string) => void;
  hasUnsavedChanges: boolean;
  setSelectedPlanId: (id: string | null) => void;
  onSetActivePlan: (planId: string, isCurrentlyActive: boolean) => void;
  isUpdatingActivePlan: boolean;
  onDeletePlan: (planId: string) => void;
  refetchAllPlans: () => void; 
}

const PlanCards: React.FC<PlanCardsProps> = ({
  allPlans,
  selectedPlanId,
  handlePlanSelect,
  hasUnsavedChanges,
  setSelectedPlanId,
  onSetActivePlan,
  isUpdatingActivePlan,
  onDeletePlan,
  refetchAllPlans, 
}) => {
  const [showAllPlans, setShowAllPlans] = useState(false);

  // Find the active plan
  const activePlan = allPlans.find(plan => plan.isActive);
  
  // Get other plans (excluding active plan)
  const otherPlans = allPlans.filter(plan => !plan.isActive);

  // Show only 3 other plans initially, or all if dropdown is expanded
  const displayedOtherPlans = showAllPlans ? otherPlans : otherPlans.slice(0, 3);

  // Handle delete with SweetAlert confirmation
  const handleDelete = (e: React.MouseEvent, planId: string, planName: string) => {
    e.stopPropagation();
    
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the plan "${planName}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'plan-cards-swal-popup',
        confirmButton: 'plan-cards-swal-confirm-btn',
        cancelButton: 'plan-cards-swal-cancel-btn'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onDeletePlan(planId);
        
        // Refetch plans to refresh the list after deletion
        refetchAllPlans();
        
        // Show success message after deletion
        Swal.fire({
          title: 'Deleted!',
          text: 'Your plan has been deleted.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // If the deleted plan was the selected plan, reset selectedPlanId
        if (selectedPlanId === planId) {
          setSelectedPlanId(null);
        }
      }
    });
  };

  return (
    <div className="plan-cards-container">
      {/* Initial Plan Card - Always visible */}
      <div
        className={`plan-card ${
          !selectedPlanId
            ? "plan-card--active"
            : "plan-card--inactive"
        }`}
        onClick={() => {
          if (hasUnsavedChanges) {
            Swal.fire({
              title: 'Unsaved Changes',
              text: "You have unsaved changes. Loading initial plan will lose these changes. Continue?",
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: 'Yes, continue!',
              cancelButtonText: 'Cancel'
            }).then((result) => {
              if (result.isConfirmed) {
                setSelectedPlanId(null);
              }
            });
          } else {
            setSelectedPlanId(null);
          }
        }}
      >
        <div className="plan-card__content">
          <GitBranch className="plan-card__icon" />
          <span className="plan-card__title">Initial Plan</span>
        </div>
        <div className="plan-card__description">
          Blueprint for Cart allocations
        </div>
      </div>

      {/* Active Plan Card - Always visible */}
      {activePlan && (
        <div
          key={activePlan.planId}
          className={`plan-card ${
            selectedPlanId === activePlan.planId
              ? "plan-card--active"
              : "plan-card--inactive"
          }`}
          onClick={() => handlePlanSelect(activePlan.planId)}
        >
          <div className="plan-card__header">
            <div className="plan-card__title-container">
              <GitBranch className="plan-card__icon" />
              <span className="plan-card__title">
                {activePlan.allocationPlanVersion}
              </span>
            </div>
            <div className="plan-card__actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSetActivePlan(activePlan.planId, activePlan.isActive);
                }}
                disabled={isUpdatingActivePlan || activePlan.isActive}
                className={`plan-card__star-button ${activePlan.isActive ? "plan-card__star-button--active" : ""}`}
                title="Active plan"
              >
                <Star
                  className={`plan-card__star-icon plan-card__star-icon--active`}
                />
              </button>
            </div>
          </div>
          <div className="plan-card__date">
            <span className="plan-card__date-text">
              {new Date(activePlan.createdAt._seconds * 1000).toLocaleDateString()}
            </span>
          </div>
          <div className="plan-card__badge">
            <span className="plan-card__active-badge">Active</span>
          </div>
        </div>
      )}

      {/* Other Plans - Limited display with dropdown */}
      {displayedOtherPlans.map((plan: any) => (
        <div
          key={plan.planId}
          className={`plan-card ${
            selectedPlanId === plan.planId
              ? "plan-card--active"
              : "plan-card--inactive"
          }`}
          onClick={() => handlePlanSelect(plan.planId)}
        >
          <div className="plan-card__header">
            <div className="plan-card__title-container">
              <GitBranch className="plan-card__icon" />
              <span className="plan-card__title">
                {plan.allocationPlanVersion}
              </span>
            </div>
            <div className="plan-card__actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSetActivePlan(plan.planId, plan.isActive);
                }}
                disabled={isUpdatingActivePlan || plan.isActive}
                className={`plan-card__star-button ${plan.isActive ? "plan-card__star-button--active" : ""}`}
                title={plan.isActive ? "Already active" : "Set as active plan"}
              >
                <Star
                  className={`plan-card__star-icon ${
                    plan.isActive ? "plan-card__star-icon--active" : ""
                  }`}
                />
              </button>
              <button
                onClick={(e) => handleDelete(e, plan.planId, plan.allocationPlanVersion)}
                className="plan-card__delete-button"
                title="Delete plan"
              >
                <Trash2 className="plan-card__delete-icon" />
              </button>
            </div>
          </div>
          <div className="plan-card__date">
            <span className="plan-card__date-text">
              {new Date(plan.createdAt._seconds * 1000).toLocaleDateString()}
            </span>
          </div>
        </div>
      ))}

      {/* Dropdown toggle for additional plans */}
      {otherPlans.length > 2 && (
        <div
          className="plan-card plan-card--dropdown-toggle"
          onClick={() => setShowAllPlans(!showAllPlans)}
        >
          <div className="plan-card__content">
            {showAllPlans ? (
              <ChevronUp className="plan-card__dropdown-icon" />
            ) : (
              <ChevronDown className="plan-card__dropdown-icon" />
            )}
            <span className="plan-card__dropdown-text">
              {showAllPlans ? 'Show Less' : `+${otherPlans.length - 2} More`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanCards;